import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchaseAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ClipboardList, Clock, CheckCircle, FileText, Truck, Package, AlertCircle, Loader2 } from 'lucide-react';

export default function PurchaseDashboard() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);

  const loadApprovals = async () => {
    try {
      const res = await purchaseAPI.listCTSApprovals();
      setApprovals(res.data?.approvals || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await purchaseAPI.dashboard();
        setStats(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
      loadApprovals();
    })();
  }, []);

  const approve = async (a) => {
    setActing(true);
    try {
      await purchaseAPI.approveCTSReceipt(a.pr_id, a.receipt_id);
      toast.success('Receipt approved — stock posted');
      loadApprovals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approve failed'); }
    finally { setActing(false); }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) { toast.error('Please enter a reason'); return; }
    setActing(true);
    try {
      await purchaseAPI.rejectCTSReceipt(rejectTarget.pr_id, rejectTarget.receipt_id, { reason: rejectReason });
      toast.success('Receipt rejected — note sent to stores');
      setRejectTarget(null); setRejectReason('');
      loadApprovals();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reject failed'); }
    finally { setActing(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;

  const cards = [
    { label: 'Items in Indent', value: stats?.indent_count || 0, icon: ClipboardList, color: 'border-l-cyan-500', path: '/purchase/indent' },
    { label: 'Pending Quotes', value: stats?.pending_quote_count || 0, icon: Clock, color: 'border-l-amber-500', path: '/purchase/indent?status=pending_quote' },
    { label: 'PRs Pending', value: stats?.purchase_request_count || 0, icon: AlertCircle, color: 'border-l-red-500', path: '/purchase/requests?status=pending_approval' },
    { label: 'Approved PRs', value: stats?.approved_pr_count || 0, icon: CheckCircle, color: 'border-l-emerald-500', path: '/purchase/requests?status=approved' },
    { label: 'PO Generated', value: stats?.po_generated_count || 0, icon: Package, color: 'border-l-blue-500', path: '/purchase/requests?status=po_generated' },
    { label: 'POs Sent', value: stats?.po_sent_count || 0, icon: Truck, color: 'border-l-purple-500', path: '/purchase/requests?status=po_sent' },
    { label: 'Partially Received', value: stats?.partially_received_count || 0, icon: FileText, color: 'border-l-orange-500', path: '/purchase/requests?status=partially_received' },
  ];

  return (
    <div className="space-y-6" data-testid="purchase-dashboard">
      <div>
        <h1 className="font-heading text-xl font-bold tracking-tight">Purchase Dashboard</h1>
        <p className="text-xs text-muted-foreground mt-1">Overview of purchasing activities</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <Card key={c.label} className={`border-l-4 ${c.color} cursor-pointer hover:shadow-md transition-shadow`}
            onClick={() => navigate(c.path)} data-testid={`dash-card-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-slate-500 font-medium tracking-wide">{c.label.toUpperCase()}</p>
                  <p className="text-2xl font-bold text-[#0F172A] mt-1">{c.value}</p>
                </div>
                <c.icon className="w-7 h-7 text-slate-200" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* CTS Receipt Approvals Inbox */}
      <Card className="border-l-4 border-l-amber-500" data-testid="cts-approvals-inbox">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" /> CTS Receipts Awaiting Approval
              <span className="text-[10px] bg-amber-500 text-white rounded-full px-2 py-0.5" data-testid="cts-approvals-count">{approvals.length}</span>
            </h2>
          </div>
          {approvals.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="cts-approvals-empty">No CTS receipts pending approval. All caught up.</p>
          ) : (
            <div className="space-y-2">
              {approvals.map(a => (
                <div key={a.receipt_id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-xs" data-testid={`cts-approval-${a.receipt_id}`}>
                  <div className="min-w-0">
                    <button className="font-medium text-amber-700 hover:underline" onClick={() => navigate(`/purchase/requests?open=${a.pr_id}`)} data-testid={`cts-approval-open-${a.receipt_id}`}>
                      {a.po_number || 'PO'} · {a.item_name}
                    </button>
                    <div className="text-[10px] text-muted-foreground">
                      {a.supplier_name} · by {a.received_by_name} · wt {a.actual_weight_kg} Kg (exp {a.expected_weight_kg}, var {a.weight_variance_pct}%) · scrap {a.scrap_kg} Kg (var {a.scrap_variance_pct}%)
                    </div>
                  </div>
                  {isAdmin ? (
                    <div className="flex items-center gap-1">
                      <Button size="sm" className="h-7 text-[11px] px-2" disabled={acting} onClick={() => approve(a)} data-testid={`inbox-approve-${a.receipt_id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 text-rose-600 border-rose-300" disabled={acting} onClick={() => { setRejectReason(''); setRejectTarget(a); }} data-testid={`inbox-reject-${a.receipt_id}`}>Reject</Button>
                    </div>
                  ) : (
                    <span className="italic text-amber-700">awaiting admin</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent data-testid="reject-cts-dialog">
          <DialogHeader><DialogTitle>Reject CTS receipt</DialogTitle></DialogHeader>
          {rejectTarget && (
            <div className="space-y-2 text-sm">
              <p className="text-xs text-muted-foreground">{rejectTarget.po_number} · {rejectTarget.item_name} — variance wt {rejectTarget.weight_variance_pct}% / scrap {rejectTarget.scrap_variance_pct}%.</p>
              <div>
                <Label className="text-xs">Reason (sent back to stores)</Label>
                <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="text-xs" placeholder="e.g. Weight mismatch too high — re-weigh and re-enter" data-testid="reject-cts-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }} data-testid="reject-cts-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={submitReject} disabled={acting} data-testid="reject-cts-confirm">
              {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
